import { useEffect, useMemo } from 'react';

import { useAppDispatch } from '../../store/hooks';
import { selectAllArchitectureDiagrams, useGetArchitectureQuery } from '../../store/services/architectureApi';
import { useGetDesignPagesQuery } from '../../store/services/designApi';
import { useGetAgentLogsQuery, useGetPlanningStatusQuery } from '../../store/services/planningRuntimeApi';
import { useGetRequirementsQuery, wireRequirementsSSE } from '../../store/services/requirementsApi';
import { useGetTasksQuery } from '../../store/services/tasksApi';
import { useGetUserFlowsQuery } from '../../store/services/userFlowApi';

import { generatedArtifactShares, type GeneratedArtifactSnapshots } from './generatedArtifactShares';

export function useGeneratedArtifactShares(
  projectId: string | null,
  timeline: GeneratedArtifactSnapshots['timeline'],
) {
  const id = projectId ?? '';
  const options = { skip: !projectId };
  const requirements = useGetRequirementsQuery(id, options).currentData;
  const flows = useGetUserFlowsQuery(id, options).currentData;
  const architecture = useGetArchitectureQuery(id, options).currentData;
  const pages = useGetDesignPagesQuery({ projectId: id, light: true, includePlanned: true }, options).currentData;
  const tasks = useGetTasksQuery(id, options).currentData;
  const planning = useGetPlanningStatusQuery(id, options).currentData;
  const agentLogs = useGetAgentLogsQuery(id, options).currentData;
  const dispatch = useAppDispatch();

  useEffect(() => projectId ? wireRequirementsSSE(projectId, dispatch) : undefined, [dispatch, projectId]);

  return useMemo(() => generatedArtifactShares({
    requirements, flows: flows?.user_flows, diagrams: selectAllArchitectureDiagrams(architecture),
    pages: pages?.design_pages, tasks, pmStepStatus: planning?.pm_step_status, agentLogs, timeline,
  }), [agentLogs, architecture, flows, pages, planning?.pm_step_status, requirements, tasks, timeline]);
}
